import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { Text } from 'designSystem/components/Text'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSearchResultsNavigation } from 'features/knowledgeDocumentViewer/contexts/SearchResultsNavigationContext'
import { toTitleCase } from 'features/knowledgeDocumentViewer/utils/textHelpers'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback } from 'react'
import styled from 'styled-components'

const NavigatorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1px;
`

const CounterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.defaultText};
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${(props) => props.theme.forecast.hoverGhostButton};
    border-radius: 0.25rem;
  }
`

const StyledDropdownContent = styled(DropdownMenuContent)`
  min-width: 20rem;
  max-height: 25rem;
`

const StyledDropdownItem = styled(DropdownMenuItem)<{ $isActive: boolean }>`
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
  background: ${(props) => (props.$isActive ? 'var(--accent)' : 'transparent')};
`

export function SearchResultsNavigator() {
  const nav = useSearchResultsNavigation()
  const { recordViewerAction } = useKnowledgeDocumentUXContext()
  const {
    currentIndex = -1,
    totalCount = 0,
    canGoPrevious = false,
    canGoNext = false,
    goToPrevious,
    goToNext,
    goToIndex,
    resultDocuments = [],
  } = nav ?? {}

  const handleSelectItem = useCallback(
    (index: number) => {
      recordViewerAction(`Selected search result ${index + 1}`)
      goToIndex?.(index)
    },
    [goToIndex, recordViewerAction]
  )

  const handlePrevious = useCallback(() => {
    recordViewerAction('Moved to previous search result')
    goToPrevious?.()
  }, [goToPrevious, recordViewerAction])

  const handleNext = useCallback(() => {
    recordViewerAction('Moved to next search result')
    goToNext?.()
  }, [goToNext, recordViewerAction])

  if (!nav || totalCount === 0) return null

  return (
    <NavigatorContainer>
      <Button
        variant='inputRaised'
        size='icon-lg'
        title='Previous search result'
        disabled={!canGoPrevious}
        onClick={handlePrevious}
        style={{ borderRadius: '0.25rem 0 0 0.25rem' }}>
        <ChevronLeft size={16} />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <CounterButton title='Browse search results'>
            <span>
              {currentIndex >= 0 ? currentIndex + 1 : '?'} of {totalCount}
            </span>
            <ChevronDown size={14} />
          </CounterButton>
        </DropdownMenuTrigger>
        <StyledDropdownContent align='end'>
          {resultDocuments.map((doc, index) => {
            const isActive = index === currentIndex
            const title = doc.documentTitle
              ? toTitleCase(doc.documentTitle)
              : doc.filename || doc.title
            const subtitleParts = [doc.system, doc.vendor, doc.docType].filter(
              Boolean
            )
            const subtitle = subtitleParts.join(' \u2022 ')

            return (
              <StyledDropdownItem
                key={doc.id}
                $isActive={isActive}
                onSelect={() => handleSelectItem(index)}>
                <Text size='sm' truncate>
                  {title}
                </Text>
                {subtitle && (
                  <Text size='xxs' color='muted' truncate>
                    {subtitle}
                  </Text>
                )}
              </StyledDropdownItem>
            )
          })}
        </StyledDropdownContent>
      </DropdownMenu>

      <Button
        variant='inputRaised'
        size='icon-lg'
        title='Next search result'
        disabled={!canGoNext}
        onClick={handleNext}
        style={{ borderRadius: '0 0.25rem 0.25rem 0' }}>
        <ChevronRight size={16} />
      </Button>
    </NavigatorContainer>
  )
}
